import {
    LightdashRequestMethodHeader,
    RequestMethod,
    type ApiError,
    type ApiResponse,
} from '@lightdash/common';
import { spanToTraceHeader, startSpan } from '@sentry/react';
import fetch from 'isomorphic-fetch';

const baseAPIUrl = import.meta.env.VITE_SITE_URL;

const defaultHeaders = {
    'Content-Type': 'application/json',
    [LightdashRequestMethodHeader]: RequestMethod.WEB_APP,
};

const handleError = (err: any): ApiError => {
    if (err.error?.statusCode && err.error?.name) return err;
    return {
        status: 'error',
        error: {
            name: 'NetworkError',
            statusCode: 500,
            message: `Could not connect to Sortment server. Please verify your network connection and server configuration.`,
            data: err,
        },
    };
};

type SortmentApiProps = {
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
    url: string;
    body: BodyInit | null | undefined;
    headers?: Record<string, string> | undefined | null;
    version?: 'v1' | 'v2';
    stream?: boolean;
    keepalive?: boolean;
};

export const sortmentApi = async <
    T extends ApiResponse['results'] | ReadableStream<Uint8Array>,
>({
    method,
    url,
    body,
    headers,
    version = 'v1',
    stream = false,
    keepalive = false,
}: SortmentApiProps): Promise<T> => {
    const apiPrefix = `${baseAPIUrl}/api/${version}`;
    let sentryTrace: string | undefined;
    // Manually create a span for the fetch request to be able to trace it in Sentry. This also enables Distributed Tracing.
    startSpan(
        {
            op: 'http.client',
            name: `API Request: ${method} ${url}`,
            attributes: {
                'http.method': method,
                'http.url': url,
                type: 'fetch',
                url,
                method,
            },
        },
        (s) => {
            sentryTrace = spanToTraceHeader(s);
        },
    );

    const finalHeaders =
        headers === null
            ? {}
            : {
                  ...defaultHeaders,
                  ...headers,
                  ...(sentryTrace ? { 'sentry-trace': sentryTrace } : {}),
              };

    const response = await fetch(`${apiPrefix}${url}`, {
        method,
        headers: finalHeaders,
        body,
        keepalive,
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw handleError(errorData);
    }

    if (stream) {
        if (!response.body) {
            throw new Error('ReadableStream not supported in this browser.');
        }
        return response.body as T;
    }

    return response.json().then((d: ApiResponse | ApiError) => {
        if (d.status === 'ok') {
            // make sure we return null instead of undefined
            // otherwise react-query will crash
            return (d.results ?? null) as T;
        } else {
            throw d;
        }
    });
};
